export type SourceRef =
  | { kind: 'member' | 'issue' | 'public'; id: number }
  | { kind: 'web'; url: string }

export type Destination = 'section' | 'membership' | 'both'

export type Brief = {
  format: 'short' | 'long' | 'deep' | 'legacy'
  sources: 'internal' | 'external' | 'both' | 'internal_first'
  guidance: string
  destination: Destination
  include: SourceRef[]
  exclude: SourceRef[]
  review_sources: boolean
}

export const FORMAT_OPTIONS = [
  { key: 'short', label: 'Short', hint: 'about 250 words · 3–5 minutes' },
  { key: 'long', label: 'Long', hint: 'about 1,000 words · 5–8 minutes' },
  { key: 'deep', label: 'Deep research', hint: '2,000+ words, researched question by question · 10–15 minutes' },
] as const

export const SOURCE_OPTIONS = [
  { key: 'internal', label: 'Our content', hint: 'Member articles, the public site and magazine issues. No web.' },
  { key: 'external', label: 'The web', hint: 'Written from the web; it will still link into the membership archive.' },
  { key: 'both', label: 'Both', hint: 'Our content first, plus the web. Flagged web-sourced when the web is used.' },
] as const

/** Titles whose membership site has a category to publish into (every title: "Member exclusives").
 *  No API exposes this, so it mirrors the server's MEMBER_CATEGORY. */
export const MEMBERSHIP_PUBLISHING_TITLES: readonly string[] = ['flight_journal', 'model_airplane_news', 'rc_car_action', 'diecast']
export const supportsMembership = (publicationSlug: string): boolean => MEMBERSHIP_PUBLISHING_TITLES.includes(publicationSlug)

/** The Publish to choices, worded with the section's own label (e.g. "Flight Line"). */
export const DESTINATION_OPTIONS = (section: string): { key: Destination; label: string; hint: string }[] => [
  { key: 'section', label: section, hint: `Free on ${section}` },
  { key: 'membership', label: 'Membership only', hint: 'Members only, under Member exclusives' },
  { key: 'both', label: 'Both', hint: `Members get the full piece; ${section} shows the summary with Read the full article →` },
]

/** Who reads it, in the brief line (spec §3): the section is the default and goes unsaid. */
export const DESTINATION_DESCRIPTION = (d: Destination, section: string): string =>
  (d === 'membership' ? 'Members only' : d === 'both' ? `Members + ${section}` : '')

/** What approving publishes, for the confirm text. */
export const publishTarget = (d: Destination | undefined, section: string): string =>
  d === 'membership' ? 'the membership site' : d === 'both' ? `the membership site and ${section}` : section

/** What unpublishing removes: only the copies that exist (public = wp_post_id, membership = member_wp_post_id). */
export const unpublishTarget = (d: { wp_post_id?: number | null; member_wp_post_id?: number | null }, section: string): string => {
  const parts = [d.member_wp_post_id ? 'the membership site' : null, d.wp_post_id ? section : null].filter(Boolean)
  return parts.length ? parts.join(' and ') : section
}

/** The retry button for a missing public half of a published "both" draft: one carrying an error (a failed
 *  insert, or a failed flip-back with wp_post_id set), or one whose member copy is out with no public copy and
 *  no error (the process stopped between the halves). The server accepts the same cases. */
export const canPublishPublicCopy = (d: {
  status: string; destination?: Destination; error?: string | null
  wp_post_id?: number | null; member_wp_post_id?: number | null
}): boolean =>
  d.status === 'published' && d.destination === 'both'
  && (!!d.error || (d.member_wp_post_id != null && d.wp_post_id == null))

/** The destination "Save as my defaults" stores. Defaults are per user, not per title: on a title without
 *  membership publishing (the column is hidden) the user's saved destination is kept, not overwritten with
 *  the section. */
export const defaultsDestination = (b: Brief, publicationSlug: string, saved: Destination | undefined): Destination =>
  supportsMembership(publicationSlug) ? b.destination : (saved ?? 'section')

/** A hidden destination column must never leave a stale value behind. */
export const withDestinationFor = (b: Brief, publicationSlug: string): Brief =>
  supportsMembership(publicationSlug) || b.destination === 'section' ? b : { ...b, destination: 'section' }

const FORMAT_LABEL: Record<Brief['format'], string> = { short: 'Short', long: 'Long', deep: 'Deep research', legacy: 'Before formats (400–600 words)' }
const SOURCE_LABEL: Record<Brief['sources'], string> = { internal: 'Our archive', external: 'The web', both: 'Both', internal_first: 'Our archive first' }

const sameRef = (a: SourceRef, b: SourceRef) =>
  a.kind === b.kind && ('url' in a ? 'url' in b && a.url === b.url : 'id' in b && a.id === b.id)

export const isWebUrl = (s: string) => {
  const trimmed = s.trim()
  return trimmed.length <= 500 && /^https?:\/\/\S+$/.test(trimmed)
}

export function addInclude(b: Brief, ref: SourceRef): { brief: Brief; error?: string } {
  // Trim web URL if present
  const normalizedRef = ref.kind === 'web' ? { ...ref, url: ref.url.trim() } : ref

  if (b.include.some((r) => sameRef(r, normalizedRef))) return { brief: b }
  if (b.include.length >= 10) return { brief: b, error: 'At most 10 pinned sources.' }
  if (normalizedRef.kind === 'web' && b.include.filter((r) => r.kind === 'web').length >= 5)
    return { brief: b, error: 'At most 5 web links.' }

  // Remove from exclude if currently blocked (last action wins)
  return {
    brief: {
      ...b,
      include: [...b.include, normalizedRef],
      exclude: b.exclude.filter((r) => !sameRef(r, normalizedRef)),
    },
  }
}

export function addExclude(b: Brief, ref: SourceRef): Brief {
  if (b.exclude.some((r) => sameRef(r, ref)) || b.exclude.length >= 30) return b
  return { ...b, exclude: [...b.exclude, ref], include: b.include.filter((r) => !sameRef(r, ref)) }
}

export function withFormat(b: Brief, format: Brief['format'], touchedReview: boolean): Brief {
  return { ...b, format, review_sources: touchedReview ? b.review_sources : format === 'deep' }
}

export function formBrief(b: Partial<Brief> & Pick<Brief, 'format' | 'sources' | 'guidance' | 'destination'>): Brief {
  return {
    ...b,
    format: b.format === 'legacy' ? 'short' : b.format,
    sources: b.sources === 'internal_first' ? 'both' : b.sources,
    include: b.include ?? [],
    exclude: b.exclude ?? [],
    review_sources: b.review_sources ?? false,
    destination: b.destination ?? 'section',
  } as Brief
}

/** One line for a brief. `section` is the title's free section, named when members and it both get the piece. */
export function describeBrief(b: Brief, section = 'the free site'): string {
  const parts = [FORMAT_LABEL[b.format], SOURCE_LABEL[b.sources], b.guidance ? 'with guidance' : null].filter(Boolean)
  if (b.include.length > 0) parts.push(`${b.include.length} pinned`)
  if (b.exclude.length > 0) parts.push(`${b.exclude.length} blocked`)
  if (b.review_sources) parts.push('review first')
  if (b.destination && b.destination !== 'section') parts.push(DESTINATION_DESCRIPTION(b.destination, section))
  return parts.join(' · ')
}

/** What the schedule saves: each run picks its own subject and nobody reviews it, so no pins, blocks or
 *  review-first (the server drops them too). */
export function scheduleBrief(b: Brief): Brief {
  return { ...b, include: [], exclude: [], review_sources: false }
}

/** Revise is offered on drafts a reviewer has not approved or queued: the server refuses the rest (409). */
export const canRevise = (status: string): boolean =>
  status === 'draft' || status === 'needs_attention' || status === 'rejected'

/** Why a Revise button is unavailable while the reviewer has unsaved edits (the revision reads the saved
 *  draft, so the edits would be lost), or null when it is available. */
export const reviseBlockedHint = (dirty: boolean): string | null => (dirty ? 'Save your edits first' : null)

/** A warning for a format change on Revise: the rewrite uses the same sources, so Deep gets no new research
 *  and a shorter format keeps the Deep outline. `chosen` is '' for "keep the current format". */
export function reviseFormatHint(current: string | undefined, chosen: string): string | null {
  if (!chosen || chosen === current) return null
  if (chosen === 'deep') return 'Deep without new research may come out short of 2,000 words'
  if (current === 'deep') return 'The research outline is kept; the piece will be shorter'
  return null
}
